import React, { useState, useEffect } from 'react'
import QRCode from 'react-qr-code'
import html2canvas from 'html2canvas'
import JsPDF from 'jspdf'
import ReactDOM from 'react-dom/client'
import ModalComponent from 'components/common/ModalComponent'
import ButtonComponent from 'components/common/ButtonComponent'

const PrintModal = ({ isOpen, onClose, workers }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedWorkers, setSelectedWorkers] = useState([])
  const [filteredWorkers, setFilteredWorkers] = useState([])

  useEffect(() => {
    if (workers) {
      setFilteredWorkers(
        workers.filter(worker =>
          worker.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          worker.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          worker.workerId.toString().includes(searchTerm)
        )
      )
    }
  }, [searchTerm, workers])

  const handleWorkerSelect = (workerId, event) => {
    if (event) event.stopPropagation()

    setSelectedWorkers(prev =>
      prev.includes(workerId) ? prev.filter(id => id !== workerId) : [...prev, workerId]
    )
  }

  const handleSelectAll = () => {
    if (selectedWorkers.length === filteredWorkers.length) {
      setSelectedWorkers([])
    } else {
      setSelectedWorkers(filteredWorkers.map(worker => worker.workerId))
    }
  }

  const generateQRsForWorkers = async () => {
    try {
      const pdf = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })

      const selectedWorkersList = workers.filter(worker =>
        selectedWorkers.includes(worker.workerId)
      )

      const qrSize = 80
      const margin = 15
      const spacing = 10
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const qrsPerRow = Math.floor((pageWidth - 2 * margin) / (qrSize + spacing))
      const qrsPerColumn = Math.floor((pageHeight - 2 * margin) / (qrSize + spacing + 20))

      for (let i = 0; i < selectedWorkersList.length; i++) {
        const worker = selectedWorkersList[i]
        const positionInPage = i % (qrsPerRow * qrsPerColumn)
        const row = Math.floor(positionInPage / qrsPerRow)
        const col = positionInPage % qrsPerRow

        if (positionInPage === 0 && i !== 0) {
          pdf.addPage()
        }

        const qrContainer = document.createElement('div')
        qrContainer.style.width = '256px'
        qrContainer.style.height = '256px'
        qrContainer.style.background = 'white'
        document.body.appendChild(qrContainer)

        const workerData = {
          id: worker.workerId,
          name: worker.firstName,
          lastName: worker.lastName,
          phone: worker.phoneNumber
        }

        const qrComponent = <QRCode value={JSON.stringify(workerData)} size={256} />

        const root = ReactDOM.createRoot(qrContainer)
        root.render(qrComponent)

        await new Promise(resolve => setTimeout(resolve, 100))

        const canvas = await html2canvas(qrContainer)
        const imgData = canvas.toDataURL('image/png')

        const x = margin + col * (qrSize + spacing)
        const y = margin + row * (qrSize + spacing + 20)

        pdf.setFontSize(14)
        pdf.setTextColor(46, 125, 50)
        const name = `${worker.firstName} ${worker.lastName}`
        const nameWidth = pdf.getStringUnitWidth(name) * 14 / pdf.internal.scaleFactor
        const nameX = x + (qrSize - nameWidth) / 2
        pdf.text(name, nameX, y + 8)

        pdf.setFontSize(12)
        pdf.setTextColor(102, 102, 102)
        const id = `ID: ${worker.workerId}`
        const idWidth = pdf.getStringUnitWidth(id) * 12 / pdf.internal.scaleFactor
        const idX = x + (qrSize - idWidth) / 2
        pdf.text(id, idX, y + 14)

        pdf.addImage(imgData, 'PNG', x, y + 20, qrSize, qrSize)

        document.body.removeChild(qrContainer)
      }

      pdf.save(`worker-qrs-${new Date().toISOString().split('T')[0]}.pdf`)
      onClose()
    } catch (error) {
      console.error('Error generating QRs:', error)
    }
  }

  // custom footer buttons (overrides default)
  const customFooter = (
    <div className='flex justify-end space-x-4'>
      <ButtonComponent onClick={onClose} customColorsStyles='bg-gray-100 text-greenPrimary hover:text-white'>
        Cancel
      </ButtonComponent>
      <ButtonComponent onClick={generateQRsForWorkers} disabled={selectedWorkers.length === 0}>
        Print Selected Cards
      </ButtonComponent>
    </div>
  )

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title='Print Worker Cards'
      customFooter={customFooter}
    >
      <div className='space-y-4 w-96'>
        <input
          type='text'
          placeholder='Search by name or worker ID...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className='w-full p-2 border rounded focus:ring focus:ring-green-200'
        />

        <div className='flex justify-between items-center'>
          <label className='flex items-center space-x-2'>
            <input
              type='checkbox'
              checked={selectedWorkers.length === filteredWorkers.length && filteredWorkers.length > 0}
              onChange={handleSelectAll}
              className='w-5 h-5'
            />
            <span className='text-sm'>Select All</span>
          </label>
          <span className='text-sm'>{selectedWorkers.length} workers selected</span>
        </div>

        <div className='grid grid-cols-1 gap-2 max-h-64 overflow-y-auto border p-2 rounded'>
          {filteredWorkers.map(worker => {
            const isSelected = selectedWorkers.includes(worker.workerId)

            return (
              <div
                key={worker.workerId}
                className={`flex items-center space-x-2 p-2 border rounded cursor-pointer ${
                  isSelected ? 'bg-green-100' : ''
                }`}
                onClick={() => handleWorkerSelect(worker.workerId)}
              >
                <input
                  type='checkbox'
                  checked={isSelected}
                  onChange={(e) => handleWorkerSelect(worker.workerId, e)}
                  className='w-5 h-5'
                  onClick={(e) => e.stopPropagation()} // Prevent double triggering
                />
                <div className='flex flex-col'>
                  <span className='text-sm font-medium'>{worker.firstName} {worker.lastName}</span>
                  <span className='text-xs text-gray-500'>ID: {worker.workerId}</span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </ModalComponent>
  )
}

export default PrintModal
